/**
 * Register the SmartTT Club service worker for installable PWA.
 *
 * OVH runs `react-scripts start` with CRA homepage under /smarttt-frontend-prod,
 * but the app routes (and install start_url) live at `/`. The SW must therefore
 * be registered at `/sw.js` with scope `/` (file served by setupProxy).
 */

function isLocalDevHost() {
  if (typeof window === "undefined") return true;
  const host = window.location.hostname;
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "[::1]" ||
    host.endsWith(".local")
  );
}

function isGcsHostedApp() {
  if (typeof window === "undefined") return false;
  return window.location.hostname === "storage.googleapis.com";
}

/** Allow SW on HTTPS deployed hosts even when webpack-dev NODE_ENV=development. */
function shouldRegisterServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return false;
  }
  if (process.env.NODE_ENV === "production") return true;
  // OVH / pilot hosts serve via `react-scripts start` (development).
  if (!isLocalDevHost() && window.isSecureContext) return true;
  return false;
}

/**
 * Resolve SW URL + scope so the worker controls the actual app pages.
 * @returns {{ swUrl: string, scope: string }}
 */
export function resolveServiceWorkerTarget() {
  if (typeof window === "undefined") {
    return { swUrl: "/sw.js", scope: "/" };
  }

  // GCS bucket hosting: pages and assets share the PUBLIC_URL basename.
  if (isGcsHostedApp()) {
    const baked = String(process.env.PUBLIC_URL || "").replace(/\/$/, "");
    const base =
      baked && baked.indexOf("%") === -1
        ? baked
        : String(window.__PUBLIC_URL__ || "").replace(/\/$/, "");
    if (base) {
      return {
        swUrl: `${base}/sw.js`,
        scope: base.endsWith("/") ? base : `${base}/`,
      };
    }
  }

  // OVH / apex / club subdomains: routes are at `/` (env-config sets __PUBLIC_URL__="").
  // Always use root SW so scope covers /profile, /home, etc.
  return { swUrl: "/sw.js", scope: "/" };
}

let registrationPromise = null;

export function registerSmartTTServiceWorker() {
  if (!shouldRegisterServiceWorker()) {
    return Promise.resolve(null);
  }
  if (registrationPromise) return registrationPromise;

  const { swUrl, scope } = resolveServiceWorkerTarget();

  const register = () =>
    navigator.serviceWorker
      .register(swUrl, { scope })
      .then((registration) => {
        registration.onupdatefound = () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.onstatechange = () => {
            if (
              installing.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              /* new SW waiting; next load picks it up */
            }
          };
        };
        // Pull the latest SW so branded manifest/icon handlers activate.
        registration.update().catch(() => undefined);
        return registration;
      })
      .catch(() => {
        registrationPromise = null;
        return null;
      });

  if (document.readyState === "complete") {
    registrationPromise = register();
  } else {
    registrationPromise = new Promise((resolve) => {
      window.addEventListener(
        "load",
        () => {
          register().then(resolve);
        },
        { once: true }
      );
    });
  }
  return registrationPromise;
}

export async function ensureSmartTTServiceWorkerReady() {
  if (!shouldRegisterServiceWorker()) return null;
  const registration = await registerSmartTTServiceWorker();
  if (!registration) return null;
  try {
    await navigator.serviceWorker.ready;
  } catch {
    /* ignore */
  }
  return registration;
}

export function unregisterSmartTTServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => registration.unregister())
    .catch(() => undefined);
}
